import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import axios from "axios";

import * as XLSX from "xlsx";

import {
  FaArrowLeft,
  FaFileExcel,
  FaMoneyBillWave,
  FaSearch,
  FaUsers,
} from "react-icons/fa";

import "./ProfitLossReport.css";


const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const getHeaders = () => {

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("access_token") ||
    "";


  const schoolId =
    localStorage.getItem("school_id") ||
    localStorage.getItem("schoolId") ||
    localStorage.getItem(
      "selected_school_id"
    ) ||
    "";


  return {

    Authorization:
      token
        ? `Bearer ${token}`
        : "",

    "X-School-Id":
      schoolId,

    Accept:
      "application/json",
  };
};


const money = (value) =>
  new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  ).format(
    Number(value || 0)
  );


const ProfitLossFeeDetailsPage = () => {

  const {
    month,
    feeType,
  } = useParams();


  const navigate =
    useNavigate();


  const location =
    useLocation();


  const decodedFeeType =
    decodeURIComponent(
      feeType || ""
    );


  const [data, setData] =
    useState({

      month:
        month,

      month_name:
        location.state
          ?.monthName ||
        month,

      fee_type:
        decodedFeeType,

      total_transactions:
        0,

      total_amount:
        0,

      transactions:
        [],
    });


  const [search, setSearch] =
    useState("");


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  /* LOAD */

  const loadData = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/profit-loss/fee-details`,

          {

            params: {

              month:
                month,

              fee_type:
                decodedFeeType,
            },

            headers:
              getHeaders(),
          }
        );


      if (
        response?.data?.status
      ) {

        const result =
          response.data.data ||
          {};


        setData({

          month:
            result.month ||
            month,

          month_name:
            result.month_name ||
            month,

          fee_type:
            result.fee_type ||
            decodedFeeType,

          total_transactions:
            Number(
              result.total_transactions ||
              0
            ),

          total_amount:
            Number(
              result.total_amount ||
              0
            ),

          transactions:
            Array.isArray(
              result.transactions
            )
              ? result.transactions
              : [],
        });

      } else {

        setError(
          response?.data?.message ||
          "Unable to load fee details."
        );
      }

    } catch (err) {

      console.error(
        err
      );


      setError(
        err?.response?.data
          ?.message ||
        "Unable to load fee details."
      );

    } finally {

      setLoading(false);
    }
  };


  useEffect(() => {

    loadData();

  }, [
    month,
    decodedFeeType,
  ]);


  /* SEARCH */

  const filteredRows =
    useMemo(() => {

      const query =
        search
          .trim()
          .toLowerCase();


      if (!query) {

        return data.transactions;
      }


      return data.transactions.filter(
        (row) => {

          const fields = [

            row.student_name,

            row.student_code,

            row.mobile,

            row.txn_id,

            row.payment_mode,

            row.payment_date,

          ];


          return fields.some(
            (field) =>

              String(
                field ||
                ""
              )
                .toLowerCase()
                .includes(
                  query
                )
          );
        }
      );

    }, [
      data.transactions,
      search,
    ]);


  /* FILTER TOTAL */

  const filteredTotal =
    useMemo(() => {

      return filteredRows.reduce(
        (
          total,
          row
        ) =>

          total +
          Number(
            row.amount ||
            0
          ),

        0
      );

    }, [
      filteredRows,
    ]);


  /* EXCEL */

  const exportExcel = () => {

    if (
      !data.transactions.length
    ) {

      return;
    }


    const rows =
      data.transactions.map(
        (
          row,
          index
        ) => ({

          "#":
            index + 1,

          "Date":
            row.payment_date ||
            "",

          "Transaction ID":
            row.txn_id ||
            "",

          "Student Code":
            row.student_code ||
            "",

          "Student Name":
            row.student_name ||
            "",

          "Mobile":
            row.mobile ||
            "",

          "Payment Mode":
            row.payment_mode ||
            "",

          "Fee Month":
            row.payment_month ||
            "",

          "Amount":
            Number(
              row.amount ||
              0
            ),
        })
      );


    rows.push({

      "#":
        "",

      "Date":
        "",

      "Transaction ID":
        "",

      "Student Code":
        "",

      "Student Name":
        `TOTAL ${data.fee_type}`,

      "Mobile":
        "",

      "Payment Mode":
        "",

      "Fee Month":
        "",

      "Amount":
        Number(
          data.total_amount ||
          0
        ),
    });


    const worksheet =
      XLSX.utils.json_to_sheet(
        rows
      );


    worksheet["!cols"] = [

      { wch: 6 },
      { wch: 14 },
      { wch: 22 },
      { wch: 16 },
      { wch: 26 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },

    ];


    const workbook =
      XLSX.utils.book_new();


    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Fee Details"
    );


    XLSX.writeFile(

      workbook,

      `${String(
        data.fee_type ||
        "Fee"
      ).replace(
        /[^a-zA-Z0-9_-]/g,
        "_"
      )}_${month}.xlsx`
    );
  };


  return (

    <div className="pl-page">


      {/* TOOLBAR */}

      <div className="pl-detail-toolbar">


        <button
          type="button"
          className="pl-back-btn"
          onClick={() =>
            navigate(
              `/admin/reports/profit-loss/${month}`
            )
          }
        >

          <FaArrowLeft />

          {data.month_name} P&L

        </button>


        <button
          type="button"
          className="pl-excel-btn"
          disabled={
            !data.transactions.length
          }
          onClick={
            exportExcel
          }
        >

          <FaFileExcel />

          Excel

        </button>

      </div>


      {error && (

        <div className="pl-error">
          {error}
        </div>

      )}


      {/* HERO */}

      <div className="pl-hero">

        <div className="pl-hero-icon">

          <FaMoneyBillWave />

        </div>


        <div>

          <span>
            FEE COLLECTION DETAILS
          </span>

          <h1>
            {data.fee_type}
          </h1>

          <p>
            {data.month_name} individual
            student collection details.
          </p>

        </div>

      </div>


      {/* SUMMARY */}

      <div className="pl-fee-detail-summary">


        <div className="pl-summary-card">

          <div className="pl-summary-icon purple">

            <FaUsers />

          </div>

          <div>

            <span>
              Transactions
            </span>

            <strong>
              {data.total_transactions}
            </strong>

          </div>

        </div>


        <div className="pl-summary-card">

          <div className="pl-summary-icon green">

            <FaMoneyBillWave />

          </div>

          <div>

            <span>
              Total Collection
            </span>

            <strong className="pl-green">

              {money(
                data.total_amount
              )}

            </strong>

          </div>

        </div>

      </div>


      {/* TABLE */}

      <div className="pl-card">


        <div className="pl-card-head">

          <div>

            <h2>
              {data.fee_type} Collection
            </h2>

            <p>
              Individual transaction
              details.
            </p>

          </div>


          <div className="pl-search-box">

            <FaSearch />

            <input
              type="text"
              value={
                search
              }
              placeholder="Search student, code, mobile, txn..."
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
            />

          </div>

        </div>


        {loading ? (

          <div className="pl-loading">

            <div className="pl-loader" />

            Loading fee details...

          </div>

        ) : (

          <div className="pl-table-wrap">

            <table className="pl-table">

              <thead>

                <tr>

                  <th>#</th>
                  <th>Date</th>
                  <th>Txn ID</th>
                  <th>Student</th>
                  <th>Code</th>
                  <th>Mobile</th>
                  <th>Fee Month</th>
                  <th>Mode</th>

                  <th className="right">
                    Amount
                  </th>

                </tr>

              </thead>


              <tbody>

                {filteredRows.map(
                  (
                    row,
                    index
                  ) => (

                    <tr
                      key={
                        row.id ||
                        index
                      }
                    >

                      <td>
                        {index + 1}
                      </td>

                      <td>
                        {row.payment_date ||
                          "-"}
                      </td>

                      <td>
                        <span className="pl-txn">
                          {row.txn_id ||
                            "-"}
                        </span>
                      </td>

                      <td>
                        <strong className="pl-student-name">

                          {row.student_name ||
                            "-"}

                        </strong>
                      </td>

                      <td>

                        <span className="pl-student-code">

                          {row.student_code ||
                            "-"}

                        </span>

                      </td>

                      <td>
                        {row.mobile ||
                          "-"}
                      </td>

                      <td>
                        {row.payment_month ||
                          "-"}
                      </td>

                      <td>
                        {row.payment_mode ||
                          "-"}
                      </td>

                      <td className="right">

                        <strong className="pl-green">

                          {money(
                            row.amount
                          )}

                        </strong>

                      </td>

                    </tr>

                  )
                )}


                {!filteredRows.length && (

                  <tr>

                    <td
                      colSpan="9"
                      className="pl-empty"
                    >
                      No transaction found.
                    </td>

                  </tr>

                )}

              </tbody>


              {!!filteredRows.length && (

                <tfoot>

                  <tr>

                    <td colSpan="8">

                      {search
                        ? "FILTERED TOTAL"
                        : `TOTAL ${String(
                            data.fee_type
                          ).toUpperCase()}`}

                    </td>

                    <td className="right pl-total-green">

                      {money(
                        search
                          ? filteredTotal
                          : data.total_amount
                      )}

                    </td>

                  </tr>

                </tfoot>

              )}

            </table>

          </div>

        )}

      </div>

    </div>
  );
};


export default ProfitLossFeeDetailsPage;